import React, { useState, useContext } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ClientsContext, ClientGroupsContext } from "@/App";
import { DIVISIONS, divisionColor } from "@/lib/divisions";
import { toast } from "@/components/ui/sonner";
import { Building2, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export const PRESET_COLORS = [
  "#3b82f6", // Blue
  "#f97316", // Orange
  "#a855f7", // Purple
  "#ec4899", // Pink
  "#ef4444", // Red
  "#f97316", // Orange
  "#f59e0b", // Amber
  "#84cc16", // Lime
  "#22c55e", // Green
  "#14b8a6", // Teal
  "#0ea5e9", // Sky
  "#64748b", // Slate
];

export default function CreateClientModal({ onClose, onCreate }) {
  const { fetchClients } = useContext(ClientsContext);
  const { clientGroups, fetchClientGroups } = useContext(ClientGroupsContext) || { clientGroups: [] };
  const [name, setName] = useState("");
  const [color, setColor] = useState(PRESET_COLORS[0]);
  const [customHex, setCustomHex] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Phase 1b — the client's home: group (optional) + division. Picking a
  // group with a division auto-selects that division (still overridable).
  const [groupId, setGroupId] = useState("");
  const [division, setDivision] = useState("metallic");

  const groupOptions = (clientGroups || []).map(g => {
    const top = g.parent_id ? (clientGroups || []).find(p => p.id === g.parent_id) : g;
    return { id: g.id, name: g.parent_id ? `${top?.name || ""} › ${g.name}` : g.name, division: top?.division || null };
  });
  const handleGroupPick = (id) => {
    setGroupId(id);
    const g = groupOptions.find(o => o.id === id);
    if (g?.division) setDivision(g.division);
  };

  const activeColor = customHex.match(/^#[0-9a-fA-F]{6}$/) ? customHex : color;

  const handleColorSwatch = (c) => {
    setColor(c);
    setCustomHex("");
  };

  const handleHexChange = (val) => {
    setCustomHex(val);
    if (val.match(/^#[0-9a-fA-F]{6}$/)) setColor(val);
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) { setError("Client name is required"); return; }
    setSaving(true);
    setError("");
    try {
      const res = await axios.post(`${API}/api/clients`, {
        name: trimmed, color: activeColor,
        division, group_id: groupId || null,
      });
      await fetchClients();
      if (groupId && fetchClientGroups) await fetchClientGroups();
      toast.success(`Client "${trimmed}" created`);
      onCreate(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to create client");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-sm p-0 overflow-hidden"
        data-testid="create-client-modal"
      >
        {/* Top accent bar */}
        <div className="h-1 w-full" style={{ backgroundColor: activeColor }} />

        <div className="px-6 pt-5 pb-0">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: activeColor + "22" }}>
                <Building2 className="w-4 h-4" style={{ color: activeColor }} />
              </div>
              <span>New Client</span>
            </DialogTitle>
          </DialogHeader>
        </div>

        <div className="px-6 py-4 space-y-5">
          {/* Name */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))] block mb-1.5">
              Client Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={e => { setName(e.target.value); setError(""); }}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleSave(); } }}
              placeholder="e.g. Halliburton, BHGE, NOV..."
              autoFocus
              className="w-full h-9 px-3 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 transition-all"
              style={{ "--tw-ring-color": activeColor }}
              data-testid="new-client-name-input"
            />
            {error && <p className="text-xs text-red-500 mt-1.5">{error}</p>}
          </div>

          {/* Home — group + division (Phase 1b). Picking a division-carrying
              group auto-selects its division; a client can never be
              division-less (defaults to Metallic). */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))] block mb-1.5">
                Client Group
              </label>
              <select
                value={groupId}
                onChange={e => handleGroupPick(e.target.value)}
                className="w-full h-9 px-2 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 transition-all cursor-pointer"
                style={{ "--tw-ring-color": activeColor }}
                data-testid="new-client-group-select"
              >
                <option value="">No group (direct)</option>
                {groupOptions.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))] block mb-1.5">
                Division
              </label>
              <select
                value={division}
                onChange={e => setDivision(e.target.value)}
                className="w-full h-9 px-2 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 transition-all cursor-pointer"
                style={{ "--tw-ring-color": divisionColor(division), color: divisionColor(division) }}
                data-testid="new-client-division-select"
              >
                {DIVISIONS.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
              </select>
            </div>
          </div>

          {/* Color */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))] block mb-2">
              Brand Color
            </label>
            {/* Preset swatches */}
            <div className="flex flex-wrap gap-2 mb-3">
              {PRESET_COLORS.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => handleColorSwatch(c)}
                  className="w-7 h-7 rounded-full border-2 transition-all flex items-center justify-center"
                  style={{
                    backgroundColor: c,
                    borderColor: activeColor === c ? "#fff" : "transparent",
                    boxShadow: activeColor === c ? `0 0 0 2px ${c}` : "none",
                    transform: activeColor === c ? "scale(1.15)" : "scale(1)",
                  }}
                  data-testid={`color-swatch-${c.replace("#", "")}`}
                >
                  {activeColor === c && <Check className="w-3 h-3 text-white drop-shadow" />}
                </button>
              ))}
            </div>
            {/* Custom hex */}
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full border border-[hsl(var(--border))]" style={{ backgroundColor: activeColor }} />
              <input
                type="text"
                value={customHex || activeColor}
                onChange={e => handleHexChange(e.target.value)}
                placeholder="#3b82f6"
                maxLength={7}
                className="flex-1 h-8 px-2.5 text-sm font-mono-data rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-1 focus:ring-orange-500"
                data-testid="color-hex-input"
              />
            </div>
          </div>

          {/* Live preview */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))] block mb-2">
              Preview
            </label>
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[hsl(var(--muted))]/40 border border-[hsl(var(--border))]">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: activeColor }}>
                {(name || "C").charAt(0).toUpperCase()}
              </div>
              <div>
                <span
                  className="inline-block text-sm font-bold px-2.5 py-1 rounded-md"
                  style={{ color: activeColor, backgroundColor: activeColor + "18" }}
                  data-testid="client-badge-preview"
                >
                  {name || "Client Name"}
                </span>
                <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5">How it appears in the pipeline</p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 pb-5 gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/80 transition-colors border border-[hsl(var(--border))]"
            data-testid="create-client-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="px-5 py-2.5 rounded-lg text-sm font-bold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: activeColor }}
            data-testid="create-client-save-btn"
          >
            {saving ? "Creating..." : "Create Client"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
